import { useState } from "react";
import { Plus, X, Tags, Landmark } from "lucide-react";

const STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];
const inp = "min-w-0 w-full bg-pb-elevated border border-pb-border px-2.5 py-1.5 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-pb-red";

export default function ProductPricingEditor({ value, onChange, testid = "ppe" }) {
  const options = value.options || [];
  const rules = value.tax_rules || [];
  const [opt, setOpt] = useState({ label: "", price: "", price_coins: "" });
  const [rule, setRule] = useState({ region: "FL", rate: "" });
  const set = (patch) => onChange({ ...value, ...patch });

  const addOpt = () => {
    const label = opt.label.trim(); const price = Number(opt.price);
    if (!label || !(price > 0)) return;
    set({ options: [...options.filter((o) => o.label !== label), { label, price, price_coins: opt.price_coins ? Number(opt.price_coins) : null }] });
    setOpt({ label: "", price: "", price_coins: "" });
  };
  const addRule = () => {
    const region = rule.region.trim().toUpperCase(); const rate = Number(rule.rate);
    if (!region || !(rate >= 0) || rule.rate === "") return;
    set({ tax_rules: [...rules.filter((r) => r.region !== region), { region, rate }] });
    setRule({ region: "FL", rate: "" });
  };

  return (
    <div className="space-y-4" data-testid={testid}>
      <div>
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5"><Tags className="w-3.5 h-3.5 text-pb-red" /> Purchase options — several prices on one listing</label>
        <p className="text-[10px] text-zinc-600 mb-2">e.g. “4-pack $12 · 12-pack $30”. Shoppers must pick one; the card shows “from $lowest”. Leave empty to sell at the single base price.</p>
        {options.length > 0 && (
          <div className="space-y-1 mb-2">
            {options.map((o) => (
              <div key={o.label} className="flex items-center gap-2 text-xs border border-pb-border/60 bg-black/30 px-2.5 py-1.5" data-testid={`${testid}-opt-${o.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span className="font-bold text-white flex-1">{o.label}</span>
                <span className="font-mono text-pb-red">${Number(o.price).toFixed(2)}</span>
                {o.price_coins ? <span className="font-mono text-amber-300">{o.price_coins} BLC</span> : null}
                <button type="button" onClick={() => set({ options: options.filter((x) => x.label !== o.label) })} className="text-zinc-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[1.4fr_1fr_1fr_auto] gap-1.5">
          <input data-testid={`${testid}-opt-label`} value={opt.label} placeholder="Option (e.g. 12-pack)" onChange={(e) => setOpt({ ...opt, label: e.target.value })} className={inp} />
          <input data-testid={`${testid}-opt-price`} type="number" step="0.01" min="0" value={opt.price} placeholder="$ price" onChange={(e) => setOpt({ ...opt, price: e.target.value })} className={inp} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOpt(); } }} />
          <input data-testid={`${testid}-opt-coins`} type="number" step="0.01" min="0" value={opt.price_coins} placeholder="BLC (opt.)" onChange={(e) => setOpt({ ...opt, price_coins: e.target.value })} className={inp} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOpt(); } }} />
          <button type="button" data-testid={`${testid}-opt-add`} onClick={addOpt} className="border border-pb-border hover:border-pb-red px-2.5"><Plus className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div>
        <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5"><Landmark className="w-3.5 h-3.5 text-pb-red" /> Sales tax by destination</label>
        <p className="text-[10px] text-zinc-600 mb-2">Tax is charged only when this item ships <b>into</b> a listed state. Pick “US — all states” for a nationwide rate. No rules = store default (7%).</p>
        {rules.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {rules.map((r) => (
              <span key={r.region} className="inline-flex items-center gap-1.5 bg-pb-elevated border border-pb-border px-2 py-0.5 text-xs" data-testid={`${testid}-tax-${r.region.toLowerCase()}`}>
                <b className="text-white">{r.region === "US" ? "US (all)" : r.region}</b><span className="font-mono text-pb-cyan">{r.rate}%</span>
                <button type="button" onClick={() => set({ tax_rules: rules.filter((x) => x.region !== r.region) })} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
        <div className="grid grid-cols-[1.4fr_1fr_auto] gap-1.5">
          <select data-testid={`${testid}-tax-region`} value={rule.region} onChange={(e) => setRule({ ...rule, region: e.target.value })} className={inp}>
            <option value="US">US — all states</option>
            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid={`${testid}-tax-rate`} type="number" step="0.001" min="0" max="25" value={rule.rate} placeholder="rate %" onChange={(e) => setRule({ ...rule, rate: e.target.value })} className={inp}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRule(); } }} />
          <button type="button" data-testid={`${testid}-tax-add`} onClick={addRule} className="border border-pb-border hover:border-pb-red px-2.5"><Plus className="w-3.5 h-3.5" /></button>
        </div>
      </div>
    </div>
  );
}
